'use client';

import { useState, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '@/lib/db';
import { softDelete, visible } from '@/lib/softDelete';
import { useIsMounted } from '@/lib/hooks';
import { useI18n } from '@/lib/i18n';
import type { InventoryItem, NeedleKind } from '@/lib/types';
import { inferCableLengthFromNotes, buildNeedleUsageMap, itemAvailability, inferNeedleKindFromInventory } from '@/lib/needleAvailability';
import BlobImage from '@/components/BlobImage';
import EmptyArchive from '@/components/redesign/EmptyArchive';
import PageSheepStrip from '@/components/redesign/PageSheepStrip';
import Modal from '@/components/Modal';
import { Card } from '@/components/ui/Card';
import { Chip } from '@/components/ui/Chip';
import { ItalicBody } from '@/components/ui/ItalicBody';
import { MastheadAddButton } from '@/components/ui/MastheadAddButton';
import { ZineHeader } from '@/components/ui/ZineHeader';
import { splitZineTitle } from '@/lib/hooks';
import { type TabType, InventoryForm, NeedleSetForm } from '@/components/InventoryForms';

export default function InventoryPage() {
  const router = useRouter();
  const [tab, setTab] = useState<TabType>('needle');
  const [showNew, setShowNew] = useState(false);
  const [showNewSet, setShowNewSet] = useState(false);
  const [selected, setSelected] = useState<InventoryItem | null>(null);
  const [editing, setEditing] = useState(false);
  const [search, setSearch] = useState('');
  const mounted = useIsMounted();
  const { t } = useI18n();

  const TAB_LABELS: Record<TabType, string> = {
    needle: t.needles,
    cable: t.cables,
    hook: t.hooks,
    accessory: t.accessoriesTab,
  };

  const EMPTY_LABELS: Record<TabType, string> = {
    needle: t.noNeedlesYet,
    cable: t.noCablesYet,
    hook: t.noHooksYet,
    accessory: t.noAccessoriesYet,
  };

  const ADD_LABELS: Record<TabType, string> = {
    needle: t.addNeedle,
    cable: t.addCable,
    hook: t.addHook,
    accessory: t.addAccessory,
  };

  const EDIT_LABELS: Record<TabType, string> = {
    needle: t.editNeedle,
    cable: t.editCable,
    hook: t.editHook,
    accessory: t.editAccessory,
  };

  const allItems = useLiveQuery(
    async () => {
      const rows = visible(await db.inventory.where('type').equals(tab).toArray());
      // 2026-05-10 build 1315 — Veronika: "Sort from smallest
      // to biggest (2mm is smaller than 7mm)." Needles sort by
      // `size` (the mm value). 2026-05-13 build 1397 — Veronika:
      // "The cables will be sorted from shortest to longest."
      // Cables sort by `length` (the cm value). Hooks/accessories
      // keep insertion order. Comma/dot decimal separators both
      // accepted (Czech locale uses "3,5mm"). Items without a
      // parsable number fall to the bottom.
      const parseNum = (s?: string): number => {
        if (!s) return Number.POSITIVE_INFINITY;
        const m = s.replace(',', '.').match(/(\d+(?:\.\d+)?)/);
        return m ? parseFloat(m[1]) : Number.POSITIVE_INFINITY;
      };
      if (tab === 'needle') {
        return [...rows].sort((a, b) => parseNum(a.size) - parseNum(b.size));
      }
      if (tab === 'cable') {
        return [...rows].sort((a, b) => parseNum(a.length) - parseNum(b.length));
      }
      return rows;
    },
    [tab]
  );
  const items = (() => {
    if (!allItems) return undefined;
    if (!search.trim()) return allItems;
    const q = search.toLowerCase();
    return allItems.filter(item =>
      item.name.toLowerCase().includes(q) ||
      item.size?.toLowerCase().includes(q) ||
      item.length?.toLowerCase().includes(q) ||
      item.material?.toLowerCase().includes(q)
    );
  })();
  const projects = useLiveQuery(async () => visible(await db.projects.toArray()));
  const needleUsage = useMemo(() => buildNeedleUsageMap(projects || []), [projects]);

  const getProjectsUsingItem = (item: InventoryItem) => {
    if (!projects) return [];
    if (item.type === 'needle') {
      const { projects: claiming } = itemAvailability(item, needleUsage);
      const ids = new Set(claiming.map(c => c.projectId));
      return projects.filter(p => p.id != null && ids.has(p.id));
    }
    return projects.filter(p => p.accessories?.some(a => a.itemId === item.id));
  };

  const handleCreate = async (data: Omit<InventoryItem, 'id' | 'createdAt'>) => {
    await db.inventory.add({ ...data, createdAt: new Date() } as InventoryItem);
    setShowNew(false);
  };

  const handleCreateSet = async (
    shared: {
      brand: string;
      setName: string;
      material: string;
      length: string;
      cableLength: string;
      kind: NeedleKind;
    },
    sizes: string[],
    cableLengths: string[],
  ) => {
    // Create one needle row per ticked size. Shared metadata (brand,
    // set name, material, tip/needle length, kind) is copied to every
    // row so (a) the "Same as set…" picker can deduplicate them and
    // (b) a future "manage set" view can group them. For
    // interchangeable sets, additionally create one cable inventory
    // row per ticked cable length so the cables show up in the Cables
    // tab and are claimable from project forms.
    const now = new Date();
    for (const size of sizes) {
      const noteParts: string[] = [];
      if (shared.kind === 'interchangeable-tip') noteParts.push('Interchangeable');
      if (shared.kind === 'fixed-circular') noteParts.push(`Circular (fixed${shared.cableLength ? `, cable: ${shared.cableLength}` : ''})`);
      await db.inventory.add({
        type: 'needle',
        name: shared.brand,
        setName: shared.setName || undefined,
        size,
        length: shared.length,
        material: shared.material,
        quantity: 1,
        notes: noteParts.length > 0 ? noteParts.join(', ') : undefined,
        needleKind: shared.kind,
        cableLength: shared.kind === 'fixed-circular' ? shared.cableLength : undefined,
        createdAt: now,
      } as InventoryItem);
    }
    if (shared.kind === 'interchangeable-tip') {
      for (const len of cableLengths) {
        await db.inventory.add({
          type: 'cable',
          name: shared.brand,
          setName: shared.setName || undefined,
          length: len,
          material: shared.material,
          quantity: 1,
          createdAt: now,
        } as InventoryItem);
      }
    }
    setShowNewSet(false);
  };

  if (!mounted) return null;

  const { head: titleHead, tail: titleTail } = splitZineTitle(t.myAccessoriesTitle);

  return (
    <>
      {/* B1 zine masthead — 2026-05-09 build 1233 — Veronika 'my
          tools also need new header'. Shared <ZineHeader> primitive. */}
      <ZineHeader
        head={titleHead}
        tail={titleTail}
        onBack={() => router.back()}
        right={<MastheadAddButton onClick={() => setShowNew(true)} />}
      />

      {/* Tabs — Veronika: same style as yarn-page sort tabs
          (by alphabet / by color / …). 4-segment grid of
          bordered ink pills, active fills black with paper text.
          (2026-05-10 build 1261) */}
      <div style={{ padding: '0 16px', marginBottom: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
          {(['needle', 'cable', 'hook', 'accessory'] as const).map(tabKey => {
            const isActive = tab === tabKey;
            return (
              <button
                key={tabKey}
                type="button"
                aria-pressed={isActive}
                onClick={() => setTab(tabKey)}
                style={{
                  width: '100%',
                  padding: '7px 2px',
                  background: isActive ? 'var(--ink)' : 'transparent',
                  color: isActive ? 'var(--paper-1)' : 'var(--ink)',
                  border: '1px solid var(--ink)',
                  fontFamily: 'var(--font-mono)',
                  fontWeight: 700,
                  fontSize: 9,
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  lineHeight: 1,
                  textAlign: 'center',
                  whiteSpace: 'nowrap',
                  borderRadius: 0,
                }}
              >
                {TAB_LABELS[tabKey]}
              </button>
            );
          })}
        </div>
      </div>

      {(allItems?.length ?? 0) > 0 && (
        <div style={{ padding: '0 16px', marginBottom: 12 }}>
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t.search}
            className="placeholder:text-[#7A7568] placeholder:uppercase placeholder:tracking-[0.14em] placeholder:text-[10px]"
            style={{
              width: '100%',
              padding: '10px 12px',
              background: 'var(--paper-2)',
              border: '1px solid var(--ink)',
              fontFamily: 'var(--font-mono)',
              fontSize: 13,
              color: 'var(--ink)',
              letterSpacing: '0.04em',
              outline: 'none',
              borderRadius: 0,
              WebkitAppearance: 'none',
            }}
          />
        </div>
      )}

      {/* Needles-tab-only: bulk-add an interchangeable set.
          Same dashed-ink "+ ADD" bar as YarnDetailModal's
          add-another-colorway. (2026-05-10 build 1259) */}
      {tab === 'needle' && (
        <div style={{ padding: '0 16px', marginBottom: 12 }}>
          <button
            type="button"
            onClick={() => setShowNewSet(true)}
            style={{
              width: '100%',
              background: 'var(--paper-2)',
              border: '1px dashed var(--ink)',
              padding: '10px 0',
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: 'var(--ink)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
            }}
          >
            <span aria-hidden style={{ fontSize: 16, fontWeight: 800, lineHeight: 1 }}>+</span>
            <span>{t.addNeedleSet}</span>
          </button>
        </div>
      )}

      <div className="px-5 space-y-2 pb-4">
        {items?.length === 0 && (allItems?.length ?? 0) > 0 && (
          <p className="text-center text-xs text-text-tertiary py-8">{t.noMatches}</p>
        )}
        {allItems?.length === 0 && (
          <EmptyArchive
            title={EMPTY_LABELS[tab]}
            hint={t.trackInventoryHint}
            ctaLabel={ADD_LABELS[tab]}
            onAction={() => setShowNew(true)}
          />
        )}
        {items?.map(item => {
          // Inventory card — B1 zine port (2026-05-10 build 1259).
          // Same row structure as the YarnRowCard / multi-colorway
          // group card on /yarn: 64px tile on the left (size/length
          // for needles / cables / hooks; photo for accessories),
          // body column with name + bordered mono-caps tag chips +
          // Fraunces italic kind line + status / project chips,
          // right-side ×qty + square ink-bordered edit button.
          const usedIn = getProjectsUsingItem(item);
          const avail = item.type === 'needle' ? itemAvailability(item, needleUsage) : null;
          const monoFamily = 'var(--font-mono)';
          const sansFamily = 'var(--font-geist-sans), system-ui, sans-serif';
          return (
            <div key={item.id} style={{ display: 'flex', alignItems: 'stretch', gap: 8 }}>
              {/* Card */}
              <Card
                style={{
                  flex: 1,
                  minWidth: 0,
                  display: 'flex',
                  alignItems: 'stretch',
                }}
              >
                {/* Left tile — size/length text or accessory photo */}
                <div
                  style={{
                    width: 64,
                    flexShrink: 0,
                    borderRight: '1px solid var(--ink)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--paper-1)',
                    overflow: 'hidden',
                  }}
                >
                  {item.type === 'accessory' && item.photo ? (
                    <BlobImage
                      src={item.photo}
                      alt={item.name}
                      data-accessory-thumb
                      className="w-full h-full object-cover"
                      lazy
                    />
                  ) : (() => {
                    // 2026-05-11 build 1319 — Veronika: split the
                    // unit ("mm" / "cm") onto its own line below
                    // the number, so e.g. "3,25mm" renders as
                    //   3,25
                    //    mm
                    // Falls back to the original single-line
                    // rendering when the value doesn't end in
                    // a recognised unit (e.g. "US 6", "—").
                    const raw = (item.type === 'cable' ? item.length : item.size) || '—';
                    const m = raw.trim().match(/^(.+?)\s*(mm|cm|in)$/i);
                    if (m) {
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1, padding: '0 4px' }}>
                          <span style={{ fontFamily: sansFamily, fontWeight: 800, fontSize: 16, letterSpacing: '-0.02em', color: 'var(--ink)' }}>{m[1]}</span>
                          <span style={{ fontFamily: monoFamily, fontWeight: 600, fontSize: 8, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-2)', marginTop: 2 }}>{m[2].toLowerCase()}</span>
                        </div>
                      );
                    }
                    return (
                      <span style={{ fontFamily: sansFamily, fontWeight: 800, fontSize: 15, letterSpacing: '-0.02em', color: 'var(--ink)', textAlign: 'center', padding: '0 4px' }}>{raw}</span>
                    );
                  })()}
                </div>

                {/* Body column */}
                <div style={{ flex: 1, minWidth: 0, padding: '8px 10px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {/* Title row */}
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                    <h3
                      style={{
                        flex: 1,
                        minWidth: 0,
                        fontFamily: sansFamily,
                        fontWeight: 800,
                        fontSize: 14,
                        letterSpacing: '-0.01em',
                        color: 'var(--ink)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        margin: 0,
                        lineHeight: 1.2,
                      }}
                    >
                      {item.name}
                    </h3>
                    <span
                      style={{
                        fontFamily: monoFamily,
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        color: 'var(--ink-2)',
                        flexShrink: 0,
                      }}
                    >
                      ×{item.quantity}
                    </span>
                  </div>

                  {/* Tag chips */}
                  {(item.size || item.length || item.material) && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {item.size && <Chip>{item.size}</Chip>}
                      {item.length && <Chip>{item.length}</Chip>}
                      {item.material && <Chip>{item.material}</Chip>}
                    </div>
                  )}

                  {/* Kind / notes line in editorial italic */}
                  {item.type === 'needle' ? (
                    <ItalicBody>
                      {(() => {
                        const kind = inferNeedleKindFromInventory(item);
                        if (kind === 'interchangeable-tip') return t.needleKindInterchangeable;
                        if (kind === 'fixed-circular') {
                          const cable = item.cableLength || inferCableLengthFromNotes(item.notes);
                          return cable ? `${t.needleKindFixedCircular} · ${cable}` : t.needleKindFixedCircular;
                        }
                        return t.needleKindStraight;
                      })()}
                    </ItalicBody>
                  ) : (
                    item.notes && (
                      <ItalicBody>
                        {item.notes}
                      </ItalicBody>
                    )
                  )}

                  {/* Status + project chips */}
                  {(avail || usedIn.length > 0) && (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'center', marginTop: 1 }}>
                      {avail && avail.state === 'conflict' && (
                        <Chip size="xs" tone="danger">⚠ {avail.claimed}/{avail.quantity}</Chip>
                      )}
                      {avail && avail.state === 'in-use' && (
                        <Chip size="xs" tone="accent">{t.availabilityInUse}</Chip>
                      )}
                      {avail && avail.state === 'available' && avail.quantity > avail.claimed && (
                        <Chip size="xs" tone="success">{t.availabilityAvailable}</Chip>
                      )}
                      {usedIn.map(p => (
                        <Chip
                          key={p.id}
                          bold
                          onClick={() => router.push(`/projects/view?id=${p.id}`)}
                        >
                          {p.name}
                        </Chip>
                      ))}
                    </div>
                  )}
                </div>
              </Card>

              {/* Edit button — square ink-bordered, B1 zine. */}
              <button
                onClick={() => { setSelected(item); setEditing(true); }}
                aria-label="Edit"
                style={{
                  width: 36,
                  flexShrink: 0,
                  border: '1px solid var(--ink)',
                  background: 'var(--paper-2)',
                  color: 'var(--ink)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
              </button>
            </div>
          );
        })}
      </div>

      <Modal open={showNew} onClose={() => setShowNew(false)} title={ADD_LABELS[tab]}>
        <InventoryForm type={tab} onSubmit={handleCreate} onCancel={() => setShowNew(false)} />
      </Modal>

      <Modal open={showNewSet} onClose={() => setShowNewSet(false)} title={t.addNeedleSet}>
        <NeedleSetForm
          existingNeedles={(allItems || []).filter(i => i.type === 'needle' && i.needleKind)}
          onSubmit={handleCreateSet}
          onCancel={() => setShowNewSet(false)}
        />
      </Modal>

      {/* Edit modal — opens directly from edit button on list */}

      {/* Item edit modal */}
      {selected && editing && (
        <Modal open={true} onClose={() => setEditing(false)} title={EDIT_LABELS[selected.type]}>
          <InventoryForm
            type={selected.type}
            initial={selected}
            onSubmit={async (data) => {
              await db.inventory.update(selected.id!, data);
              const updated = await db.inventory.get(selected.id!);
              if (updated) setSelected(updated);
              setEditing(false);
            }}
            onCancel={() => setEditing(false)}
            onDelete={async () => {
              // softDelete sets `deletedAt`; reverse-sync propagates
              // the tombstone to peers. Item disappears from the
              // visible list via the `visible()` filter on
              // useLiveQuery. Recoverable in principle (clear
              // deletedAt) but no UI for that — Veronika's mental
              // model is "I broke it, it's gone".
              const deletedId = selected.id!;
              await softDelete(db.inventory, deletedId);
              // 2026-05-10 build 1290 — Veronika: "When I deleted
              // one of my needles, it stays in the project, it
              // shouldn't." ProjectNeedle carries an
              // inventoryItemId back-link; cascade the delete by
              // dropping every needle row that points at the
              // just-deleted inventory item from every project's
              // needles[] array. Manually-typed needles (no
              // inventoryItemId) are left alone.
              const allProjects = await db.projects.toArray();
              for (const proj of allProjects) {
                if (!proj.needles || proj.needles.length === 0) continue;
                const filtered = proj.needles.filter(n => n.inventoryItemId !== deletedId);
                if (filtered.length !== proj.needles.length) {
                  await db.projects.update(proj.id!, { needles: filtered, updatedAt: new Date() });
                }
              }
              setSelected(null);
              setEditing(false);
            }}
          />
        </Modal>
      )}

      <PageSheepStrip />
    </>
  );
}

